import { Carousel, Modal } from 'react-bootstrap';
import { KTSVG } from '../../../../_metronic/helpers';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

interface Props extends React.ComponentProps<typeof Modal> {
  title: string;
  imagePaths: string[];
  show: boolean;
  onHide: () => void;
  imageAlt?: string;
  renderSelectedImageOverlayContent?: (selectedImageIndex: number) => React.ReactNode;
  bodyClassName?: string;
}

export default function ImageListModal({
  title,
  imagePaths,
  show,
  onHide,
  renderSelectedImageOverlayContent,
  bodyClassName,
  ...props
}: Props) {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const imagesThumbnailRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (imagesThumbnailRef.current) {
        const { scrollWidth, clientWidth } = imagesThumbnailRef.current;
        setIsOverflowing(scrollWidth > clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => window.removeEventListener('resize', checkOverflow);
  }, [imagePaths]);

  const onClose = () => {
    setSelectedImageIndex(0);
    onHide();
  };

  return (
    <Modal
      id='kt_modal_buyer_picture'
      tabIndex={-1}
      size='xl'
      centered
      show={show}
      onHide={onClose}
      {...props}
    >
      <div className='container-xxl px-10 py-10'>
        <div className='modal-header py-2 d-flex border-0'>
          {/* begin::Close */}
          <div>
            <h1 className='d-flex align-items-center text-dark fw-bolder my-1 fs-3'>{title}</h1>
          </div>
          <div className=' mr-auto btn btn-icon btn-sm btn-light-primary' onClick={onClose}>
            <KTSVG className='svg-icon-2' path='/media/icons/duotune/arrows/arr061.svg' />
          </div>
          {/* end::Close */}
        </div>
        <div className='modal-body'>
          <div className={bodyClassName ?? 'position-relative mb-2 text-center'}>
            {/* Selected image */}
            <div className='overlay d-inline-block'>
              <div className='overlay-wrapper'>
                <img
                  src={imagePaths?.[selectedImageIndex]}
                  alt={`${title} ${selectedImageIndex + 1}`}
                  className='w-auto h-auto'
                  style={{ maxHeight: '100%', maxWidth: '100%' }}
                />
              </div>
              {renderSelectedImageOverlayContent?.(selectedImageIndex) && (
                <div className='overlay-layer bg-dark bg-opacity-10 rounded'>
                  {renderSelectedImageOverlayContent(selectedImageIndex)}
                </div>
              )}
            </div>

            {/* Navigation buttons */}
            {selectedImageIndex > 0 && (
              <button
                type='button'
                className='btn btn-dark btn-icon btn-sm bg-dark bg-opacity-75 position-absolute start-0 top-50 translate-middle-y'
                onClick={(e) => {
                  setSelectedImageIndex((prevIndex) => prevIndex - 1);
                  e.currentTarget.blur();
                }}
              >
                <i className='fas fa-chevron-left'></i>
              </button>
            )}
            {selectedImageIndex < imagePaths?.length - 1 && (
              <button
                type='button'
                className='btn btn-dark btn-icon btn-sm bg-dark bg-opacity-75 position-absolute end-0 top-50 translate-middle-y'
                onClick={(e) => {
                  setSelectedImageIndex((prevIndex) => prevIndex + 1);
                  e.currentTarget.blur();
                }}
              >
                <i className='fas fa-chevron-right'></i>
              </button>
            )}
          </div>

          {/* Thumbnail/preview images */}
          <div className='overflow-auto'>
            <div
              ref={imagesThumbnailRef}
              className={clsx(
                'd-flex align-items-center gap-2 py-2',
                isOverflowing ? 'justify-content-start' : 'justify-content-center'
              )}
            >
              {imagePaths?.map((path, index) => (
                <div
                  key={index}
                  className={clsx(
                    'symbol symbol-50px symbol-2by3 justify-self-center border border-2 border-primary border-active rounded-3',
                    index === selectedImageIndex && 'active'
                  )}
                  style={{ padding: '1px' }}
                >
                  <div
                    className='symbol-label cursor-pointer'
                    onClick={() => setSelectedImageIndex(index)}
                    style={{
                      backgroundImage: `url('${path}')`,
                    }}
                  ></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
